import React, { useEffect, useMemo } from 'react';
import { Switch, useParams, Redirect, Route, useHistory } from 'react-router-dom';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { user } from '../../../redux/user';
import { managementCompany, setManagementCompany } from '../../../redux/managementCompany';
import ProtectedRoute from '../../../sections/ProtectedRoute';
import utils from '../../../utils/utils';
import { PERMISSION, PRIVATE_PATH } from '../../../constants';
import MANAGEMENT_COMPANIES_PERMISSIONS from '../../../constants/ManagementCompaniesPermissions';
import WithOnlyOnDesktop from '../../../components/onlyOnDesktop/withOnlyOnDesktop';
import Sidebar from '../../../components/sidebar/Sidebar';
import SettingsMultiIcon from '../../../components/icons/SettingsMultiIcon';
import Select from '../../../components/form/Select';
import NotificationsSettings from './NotificationsSettings';

import './ManagementCompanySettings.scss';

const ManagementCompanySettings = () => {
	const dispatch = useDispatch();
	const history = useHistory();
	const params = useParams();
	const userData = useSelector(user, shallowEqual);
	const managementCompanyData = useSelector(managementCompany, shallowEqual);
	const managementCompanyId = params.managementCompanyId || managementCompanyData?.id;

	const sidebarItems = [
		{
			label: 'Notifications',
			path: `${PRIVATE_PATH.COMPANY_SETTINGS}/${managementCompanyId}/notifications`,
			isFlexOnly: true
		}
	];

	const managementCompaniesOptions = useMemo(() => {
		const uniqueManagementCompanyIds = new Set();
		const options = [];

		userData?.facilitiesEnabled?.forEach((facility) => {
			const mcId = facility.management_company_id;
			if (
				!uniqueManagementCompanyIds.has(mcId) &&
				utils.isAuthorizedExact(
					MANAGEMENT_COMPANIES_PERMISSIONS.SETTINGS_PAGE_VIEW,
					userData.managementCompanyPermissions[mcId]
				)
			) {
				uniqueManagementCompanyIds.add(mcId);
				options.push({
					value: mcId,
					label: facility.management_company_name
				});
			}
		});
		return options.sort((a, b) => a.label.localeCompare(b.label));
	}, [userData]);

	const handleManagementCompanyChange = (mgComp) => {
		dispatch(setManagementCompany({ id: mgComp.value, name: mgComp.label }));
		history.push(`${PRIVATE_PATH.COMPANY_SETTINGS}/${mgComp.value}/notifications`);
	};

	useEffect(() => {
		if (!managementCompanyData?.id && managementCompaniesOptions.length) {
			if (managementCompanyId) {
				const mc = managementCompaniesOptions.find((item) => item.value === Number(managementCompanyId));
				if (mc) {
					handleManagementCompanyChange(mc);
					return;
				}
			}
			handleManagementCompanyChange(managementCompaniesOptions[0]);
		}
	}, [managementCompaniesOptions, managementCompanyId]);

	const onSelectManagementCompany = (selectedOption) => {
		const mc = managementCompaniesOptions.find(
			(item) => Number(item.value) === Number(selectedOption.managementCompany)
		);
		handleManagementCompanyChange(mc);
	};

	return (
		<WithOnlyOnDesktop>
			<div className="management-company-settings">
				<div className="settings-sidebar">
					<div className="sidebar-header">
						<SettingsMultiIcon />
						<span>Company Settings</span>
					</div>
					{managementCompaniesOptions?.length > 1 && (
						<div className="management-company-select-container">
							<div className="mc-select-label">Company</div>
							<Select
								className="management-company-select"
								handleChange={onSelectManagementCompany}
								name="managementCompany"
								options={managementCompaniesOptions}
								value={Number(managementCompanyId)}
								isSearchable={false}
							/>
						</div>
					)}
					<Sidebar className="sidebar">
						{sidebarItems.map(({ label, path }) => (
							<Sidebar.Link key={path} label={label} to={path} />
						))}
					</Sidebar>
				</div>

				<div className="settings-container">
					<Switch>
						<ProtectedRoute
							exact
							component={NotificationsSettings}
							pathPermission={PERMISSION.SETTINGS_PAGE_VIEW}
							userPermissions={userData.managementCompanyPermissions[managementCompanyId]}
							path={`${PRIVATE_PATH.COMPANY_SETTINGS}/:managementCompanyId/notifications`}
						/>
						{managementCompanyId && (
							<Route
								path={PRIVATE_PATH.COMPANY_SETTINGS}
								render={() => <Redirect to={`${PRIVATE_PATH.COMPANY_SETTINGS}/${managementCompanyId}/notifications`} />}
							/>
						)}
					</Switch>
				</div>
			</div>
		</WithOnlyOnDesktop>
	);
};

export default ManagementCompanySettings;
